import { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toString } from 'lodash';

import { api } from '@/api';
import { RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';
import { IDependentInfoDtoRelationshipTypeEnum } from '@/types';

export enum BeneficiaryType {
  PRIMARY = 'PRIMARY',
  CONTINGENT = 'CONTINGENT',
}
interface BeneficiaryAddressPayload {
  id?: number;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
}
interface BeneficiaryAddress {
  id?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
}
interface BeneficiaryPayload {
  id?: number;
  name?: string;
  beneficiary_type?: BeneficiaryType;
  birth_on?: Date;
  ssn?: string;
  tax_id?: string;
  relationship_type?: IDependentInfoDtoRelationshipTypeEnum;
  address?: BeneficiaryAddressPayload;
  allocation_percentage?: number;
  employee_id?: number;
}
export interface Beneficiary {
  id?: string;
  name?: string;
  beneficiaryType?: BeneficiaryType;
  birthOn?: string;
  ssn?: string;
  taxId?: string;
  relationshipType?: IDependentInfoDtoRelationshipTypeEnum;
  address?: BeneficiaryAddress;
  allocationPercentage?: string;
  employeeId?: string;
}

export const GET_BENEFICIARIES_QUERY_KEY = 'GET_BENEFICIARIES_QUERY_KEY';

const useGetBeneficiariesQuery = (enabled?: boolean) => {
  const {
    params: { id: employeeId },
  } = useRouteMatch<{ id: string }>();
  const { data, isLoading } = useQuery(
    [GET_BENEFICIARIES_QUERY_KEY, employeeId],
    // TODO: type is missing
    () => api.get<BeneficiaryPayload[]>(API_URL.EMPLOYEES_BENEFICIARIES_BY_EMPLOYEE_ID(employeeId)),
    { enabled: Boolean(employeeId && enabled) }
  );

  const formattedData = useMemo(
    () =>
      data?.data?.map(
        (beneficiary: BeneficiaryPayload): Beneficiary => ({
          id: toString(beneficiary?.id),
          name: beneficiary?.name,
          beneficiaryType: beneficiary?.beneficiary_type,
          ...(beneficiary?.birth_on
            ? {
                birthOn: dayjs(beneficiary.birth_on).format(RAW_DATE_FORMAT),
              }
            : {}),
          ssn: beneficiary?.ssn,
          taxId: beneficiary?.tax_id,
          relationshipType: beneficiary?.relationship_type,
          ...(beneficiary?.address
            ? {
                address: {
                  ...beneficiary.address,
                  id: toString(beneficiary.id),
                },
              }
            : {}),
          allocationPercentage: toString(beneficiary?.allocation_percentage),
          employeeId: toString(beneficiary?.employee_id),
        })
      ) || [],
    [data]
  );

  return {
    formattedData,
    isLoading,
  };
};

export default useGetBeneficiariesQuery;
